/**
 * The ONE shared settings surface — a floating, non-modal, drag-to-reposition
 * panel (the Fibonacci / Chart-Properties chrome; no dimming backdrop, chart
 * stays live). Every tool's settings (and, later, Chart Properties) render
 * through this. A tool supplies only the title, optional scope chrome, footer
 * actions, and the body (children = FieldList or a custom block).
 *
 * Footer model: live-apply + Save / Cancel. Save persists; Cancel/× restore
 * the on-open snapshot (the caller owns the snapshot via the scope engine).
 *
 * Shared skin lives in tools/_shared/panel.module.css.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useAppSelector } from "@/state/hooks";
import {
  MoreActionsMenu,
  type MoreAction,
} from "@/components/MoreActionsMenu/MoreActionsMenu";
import type { ScopeLevel } from "./registry";
import styles from "./panel.module.css";

/** Header Scope dropdown. The body owns any inherit/Customize state (it varies
 *  per tool), so this is purely the selector. */
export interface ScopeChrome {
  options: { value: ScopeLevel; label: string }[];
  current: ScopeLevel;
  onChange: (s: ScopeLevel) => void;
}

interface Props {
  open: boolean;
  title: string;
  /** Present for multi-scope tools — renders the Scope dropdown. */
  scope?: ScopeChrome;
  actions?: MoreAction[];
  /** Disable Save/Cancel + show "Saving…" while a server save is in flight. */
  saving?: boolean;
  /** Restore the on-open snapshot + close (× and Cancel). */
  onCancel: () => void;
  /** Persist + close (Save). */
  onSave: () => void;
  /** Optional extra class on the scrolling body (e.g. a roomier row variant). */
  bodyClassName?: string;
  children: ReactNode;
}

export function SettingsPanel({
  open,
  title,
  scope,
  actions,
  saving = false,
  onCancel,
  onSave,
  bodyClassName,
  children,
}: Props) {
  // Pin the panel's color-scheme to the app theme so native <select> popups
  // (scope / number fields) follow light/dark — the panel portals over the
  // chart, whose subtree forces color-scheme:dark.
  const appTheme = useAppSelector((s) => s.appearance.theme);
  // Drag-to-reposition — same pattern as FibLevelsDialog.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origTop: number;
    origLeft: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (open) setPos(null);
  }, [open]);

  // Esc cancels.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const onHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    const tgt = e.target as HTMLElement;
    if (tgt.closest("button, input, select")) return;
    e.preventDefault();
    const rect = (
      e.currentTarget.closest(`.${styles.dialog}`) as HTMLElement | null
    )?.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect?.left ?? 0,
      origTop: rect?.top ?? 0,
    };
    setPos({ top: rect?.top ?? 0, left: rect?.left ?? 0 });
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const margin = 40;
      setPos({
        top: Math.max(
          0,
          Math.min(window.innerHeight - margin, d.origTop + (e.clientY - d.startY)),
        ),
        left: Math.max(
          margin - 500,
          Math.min(window.innerWidth - margin, d.origLeft + (e.clientX - d.startX)),
        ),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  if (!open) return null;

  // Portal to <body> so the dialog's `position: fixed` is resolved against
  // the viewport. Rendered in-tree (e.g. from the DrawingToolbar, which App
  // wraps in a `transform: translateX(-50%)` div), a transformed ancestor
  // would become the containing block — making fixed coords jump on drag.
  return createPortal(
    <div
      className={styles.dialog}
      role="dialog"
      aria-label={title}
      style={{
        colorScheme: appTheme,
        ...(pos ? { top: pos.top, left: pos.left, transform: "none" } : {}),
      }}
    >
      <header
        className={`${styles.header} ${dragging ? styles.headerDragging : ""}`}
        onMouseDown={onHeaderMouseDown}
      >
        <h2 className={styles.title}>{title}</h2>
        {scope && scope.options.length > 1 && (
          <label className={styles.scopeLabel}>
            <span className={styles.scopeLabelText}>Scope</span>
            <select
              className={styles.scopeSelect}
              value={scope.current}
              onChange={(e) => scope.onChange(e.target.value as ScopeLevel)}
            >
              {scope.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onCancel}
          aria-label="Close"
        >
          ×
        </button>
      </header>

      <div className={`${styles.body}${bodyClassName ? ` ${bodyClassName}` : ""}`}>
        {children}
      </div>

      <footer className={styles.footer}>
        {actions && actions.length > 0 ? (
          <MoreActionsMenu items={actions} triggerTitle="More actions" />
        ) : null}
        <span className={styles.footerSpacer} />
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </footer>
    </div>,
    document.body,
  );
}
